import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiHeart, FiInfo, FiPlus, FiRefreshCw, FiRotateCcw, FiX, FiZap } from "react-icons/fi";
import { useAuth } from "../AuthContext";
import useOrbitDeck, { deckKey } from "../useOrbitDeck";
import "./Hunch.css";

const mediaTypeOf = (item) => item.media_type || item.mediaType || (item.title ? "movie" : "tv");
const mediaTitle = (item) => item.title || item.name || "Untitled";
const MODES = [["all", "All titles"], ["movie", "Movies"], ["tv", "Series"]];
const isTyping = (target) => target?.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target?.tagName || "");

export const DiscoveryDeck = ({ item, nextItem, onSkip, onLike, onLater, onOpen }) => {
  const pointerStart = useRef(null);
  const [dragX, setDragX] = useState(0);
  const [leaving, setLeaving] = useState("");
  const actionTimer = useRef(null);
  const acting = useRef(false);
  useEffect(() => () => clearTimeout(actionTimer.current), []);

  useEffect(() => { setDragX(0); setLeaving(""); }, [item?.id]);

  const commit = (direction, action) => {
    if (acting.current) return;
    acting.current = true;
    setLeaving(direction);
    actionTimer.current = window.setTimeout(() => {
      action();
    }, window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 0 : 240);
  };

  // Arrow keys mirror the swipe gestures so the deck is just as fast on desktop.
  useEffect(() => {
    if (!item) return undefined;
    const handleKey = (event) => {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || isTyping(event.target)) return;
      if (event.key === "Enter") {
        if (event.target.closest?.("button, a")) return;
        event.preventDefault();
        onOpen();
        return;
      }
      const action = { ArrowLeft:["left", onSkip], ArrowRight:["right", onLike], ArrowUp:["up", onLater] }[event.key];
      if (!action) return;
      event.preventDefault();
      commit(...action);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  });

  const finishDrag = (event) => {
    const start = pointerStart.current;
    pointerStart.current = null;
    if (!start || start.id !== event.pointerId || acting.current) return;
    const distance = event.clientX - start.x;
    const vertical = event.clientY - start.y;
    if (Math.abs(distance) > Math.abs(vertical) && distance > 82) commit("right", onLike);
    else if (Math.abs(distance) > Math.abs(vertical) && distance < -82) commit("left", onSkip);
    else setDragX(0);
  };

  if (!item) return null;
  const tilt = Math.max(-11, Math.min(11, dragX / 17));

  return (
    <div className="discovery-deck">
      <div className="deck-ambient" aria-hidden="true" />
      {nextItem && (
        <article className="discovery-card is-next" aria-hidden="true">
          <img src={`https://image.tmdb.org/t/p/w500${nextItem.poster_path}`} alt="" />
        </article>
      )}
      <article
        className={`discovery-card is-active ${leaving ? `is-leaving-${leaving}` : dragX ? "is-dragging" : ""}`}
        style={{ '--deck-drag-x': `${dragX}px`, '--deck-tilt': `${tilt}deg`, transform: leaving ? undefined : `translate3d(${dragX}px,0,0) rotate(${tilt}deg)` }}
        onPointerDown={(event) => {
          if (acting.current || pointerStart.current || event.button !== 0 || event.isPrimary === false || event.target.closest("button")) return;
          pointerStart.current = { x:event.clientX, y:event.clientY, id:event.pointerId };
          event.currentTarget.setPointerCapture?.(event.pointerId);
        }}
        onPointerMove={(event) => {
          const start = pointerStart.current;
          if (!start || start.id !== event.pointerId || acting.current) return;
          const distance = event.clientX - start.x;
          if (Math.abs(event.clientY - start.y) > Math.abs(distance)) { setDragX(0); return; }
          setDragX(Math.max(-180, Math.min(180, distance)));
        }}
        onPointerUp={finishDrag}
        onPointerCancel={() => {pointerStart.current = null;setDragX(0);}}
        onLostPointerCapture={() => {pointerStart.current = null;if (!acting.current) setDragX(0);}}
      >
        <img src={`https://image.tmdb.org/t/p/w500${item.poster_path}`} alt={mediaTitle(item)} draggable="false" decoding="async" />
        <strong className="swipe-stamp swipe-nope" style={{ opacity: Math.max(0, -dragX / 80) }}>NOPE</strong>
        <strong className="swipe-stamp swipe-like" style={{ opacity: Math.max(0, dragX / 80) }}>LIKE</strong>
        <div className="discovery-copy">
          <small>{mediaTypeOf(item)==="movie"?"MOVIE":"SERIES"} · {item.vote_average > 0 ? `${item.vote_average.toFixed(1)}/10 TMDB` : "Not rated"}</small>
          <h3>{mediaTitle(item)}</h3>
          <p>{item.overview}</p>
        </div>
      </article>
      <div className="deck-actions">
        <button type="button" disabled={!!leaving} className="deck-skip" onClick={() => commit("left", onSkip)} aria-label="Skip"><FiX /><span>Pass</span></button>
        <button type="button" disabled={!!leaving} className="deck-info" onClick={onOpen} aria-label="View details"><FiInfo /><span>Details</span></button>
        <button type="button" disabled={!!leaving} className="deck-like" onClick={() => commit("right", onLike)} aria-label="Like"><FiHeart /><span>Like</span></button>
        <button type="button" disabled={!!leaving} className="deck-later" onClick={() => commit("up", onLater)} aria-label="Watch later"><FiPlus /><span>Save</span></button>
      </div>
      <p className="deck-hint"><span>←</span> drag to pass <i /> drag to like <span>→</span></p>
    </div>
  );
};

/** Hunch: rapid, gut-instinct discovery — one poster at a time. */
export default function Hunch() {
  const { user, authReady, openAuth, libraryItems, experience, setLibraryFlag } = useAuth();
  const navigate = useNavigate();
  const deck = useOrbitDeck({ userId:user?.uid, library:libraryItems, experience });
  const [message, setMessage] = useState("");
  const [tally, setTally] = useState({ liked:0, saved:0, passed:0 });
  const active = deck.queue[0];
  const next = deck.queue[1];

  const savePick = async (flag) => {
    const selected = active;
    const counter = flag === "liked" ? "liked" : "saved";
    deck.consume(selected);
    setMessage("Saving your pick…");
    try {
      if (await setLibraryFlag(selected, mediaTypeOf(selected), flag)) {
        setTally(previous => ({ ...previous, [counter]:previous[counter] + 1 }));
        setMessage(flag === "liked" ? "Added to your likes." : "Saved to watch later.");
      } else { deck.restore(selected); setMessage("Could not save this title. Returned it to your deck to try again."); }
    } catch { deck.restore(selected); setMessage("Could not save this title. Returned it to your deck to try again."); }
  };
  const pass = () => {
    deck.consume(active, true);
    setTally(previous => ({ ...previous, passed:previous.passed + 1 }));
    setMessage("Passed. You can undo your last pass.");
  };
  const undo = () => {
    deck.undo();
    setTally(previous => ({ ...previous, passed:Math.max(0, previous.passed - 1) }));
    setMessage("Last pass restored.");
  };

  if (!authReady) return <main className="hunch-page hunch-gate"><span className="hunch-loader" role="status" aria-label="Loading" /></main>;
  if (!user) return (
    <main className="hunch-page hunch-gate">
      <div><FiZap /><small>HUNCH · RAPID DISCOVERY</small><h1>Trust your hunch.</h1><p>Sign in to swipe through fresh titles and keep the ones that grab you.</p><button onClick={openAuth}>Continue with Google</button></div>
    </main>
  );

  const hour = new Date().getHours();
  const moment = new Date().getDay() === 5 ? "Friday-night hunches" : hour >= 20 ? "Tonight's hunches" : "What's your hunch?";

  return (
    <main className="hunch-page">
      <section className="hunch-intro">
        <span className="hunch-eyebrow"><FiZap /> HUNCH · RAPID DISCOVERY</span>
        <h1>Trust your <em>hunch.</em></h1>
        <p>One poster, one instinct. Pass, like or save in a heartbeat — liked, passed and watched titles never come back around on this device.</p>
        <div className="hunch-modes" role="group" aria-label="Discovery type">
          {MODES.map(([value, label]) => <button key={value} aria-pressed={deck.mode === value} disabled={deck.mode === value} onClick={() => { deck.changeMode(value); setMessage(""); }}>{label}</button>)}
        </div>
        <dl className="hunch-tally" aria-label="This session">
          <div><dt>Liked</dt><dd>{tally.liked}</dd></div>
          <div><dt>Saved</dt><dd>{tally.saved}</dd></div>
          <div><dt>Passed</dt><dd>{tally.passed}</dd></div>
          <div><dt>Explored</dt><dd>{deck.reviewed}</dd></div>
        </dl>
        <ul className="hunch-keys" aria-label="Keyboard shortcuts">
          <li><kbd>←</kbd> Pass</li><li><kbd>→</kbd> Like</li><li><kbd>↑</kbd> Save</li><li><kbd>Enter</kbd> Details</li>
        </ul>
      </section>

      <section className="hunch-stage" aria-label={moment}>
        <h2 className="hunch-moment">{moment}</h2>
        {active ? <DiscoveryDeck
          key={deckKey(active)}
          item={active}
          nextItem={next}
          onSkip={pass}
          onLike={() => savePick("liked")}
          onLater={() => savePick("watchlist")}
          onOpen={() => navigate(`/${mediaTypeOf(active)}/${active.id}`)}
        /> : <div className="deck-empty" role="status"><FiZap /><h3>{deck.busy ? "Finding your next hunch…" : "You're caught up with this batch"}</h3><p>{deck.busy ? "Looking for titles you haven't explored." : "Try another category or explore further. We won't loop old picks."}</p></div>}
        <div className="deck-footer">
          <button disabled={!deck.lastPassed || deck.busy} onClick={undo}><FiRotateCcw />Undo pass</button>
          {deck.more && <button disabled={deck.busy} onClick={deck.loadMore}><FiRefreshCw className={deck.busy ? "hunch-spin" : ""} />{deck.busy ? "Finding picks…" : deck.error ? "Retry loading" : "Explore further"}</button>}
        </div>
        <p className="deck-feedback" role="status">{deck.error || message || `${deck.queue.length} fresh titles ready to explore.`}</p>
      </section>
    </main>
  );
}
